import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Dice5, X } from 'lucide-react';
import { Buton, Etiket, Girdi, toast } from '@emanetx/ui';
import type { Kupon, KuponIndirimTipi, KuponTipi } from '@emanetx/sdk';
import { useKuponGuncelle, useKuponOlustur } from './api.js';

const TIP_SECENEK: { d: KuponTipi; e: string }[] = [
  { d: 'COK_KULLANIMLIK', e: 'Çok kullanımlık' },
  { d: 'TEKIL', e: 'Tek seferlik' },
  { d: 'ILK_KULLANICI', e: 'İlk kullanıcı' },
  { d: 'PARTNER_OZEL', e: 'Partner özel' },
];

function rastgeleKod(uzunluk = 8): string {
  const harf = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < uzunluk; i++) {
    s += harf[Math.floor(Math.random() * harf.length)];
  }
  return s;
}

function tarihYMD(d: Date): string {
  return d.toISOString().slice(0, 16);
}

interface Ozellikler {
  acik: boolean;
  onDegisim: (a: boolean) => void;
  kupon?: Kupon | null;
}

export function KuponDialog({ acik, onDegisim, kupon }: Ozellikler) {
  const duzenleme = !!kupon;
  const olustur = useKuponOlustur();
  const guncelle = useKuponGuncelle();

  const [kod, setKod] = useState('');
  const [tip, setTip] = useState<KuponTipi>('COK_KULLANIMLIK');
  const [indirimTipi, setIndirimTipi] = useState<KuponIndirimTipi>('YUZDE');
  const [indirimDegeri, setIndirimDegeri] = useState('');
  const [maxIndirimTutar, setMaxIndirimTutar] = useState('');
  const [minKiralamaTutar, setMinKiralamaTutar] = useState('');
  const [toplamLimit, setToplamLimit] = useState('');
  const [musteriBasinaLimit, setMusteriBasinaLimit] = useState(1);
  const [gecerliBaslangic, setGecerliBaslangic] = useState('');
  const [gecerliBitis, setGecerliBitis] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [aktif, setAktif] = useState(true);

  useEffect(() => {
    if (!acik) return;
    if (kupon) {
      setKod(kupon.kod);
      setTip(kupon.tip);
      setIndirimTipi(kupon.indirimTipi);
      setIndirimDegeri(kupon.indirimDegeri);
      setMaxIndirimTutar(kupon.maxIndirimTutar ?? '');
      setMinKiralamaTutar(kupon.minKiralamaTutar ?? '');
      setToplamLimit(kupon.toplamLimit?.toString() ?? '');
      setMusteriBasinaLimit(kupon.musteriBasinaLimit);
      setGecerliBaslangic(tarihYMD(new Date(kupon.gecerliBaslangic)));
      setGecerliBitis(tarihYMD(new Date(kupon.gecerliBitis)));
      setAciklama(kupon.aciklama);
      setAktif(kupon.aktif);
    } else {
      const bugun = new Date();
      const sonraki = new Date();
      sonraki.setMonth(sonraki.getMonth() + 3);
      setKod(rastgeleKod());
      setTip('COK_KULLANIMLIK');
      setIndirimTipi('YUZDE');
      setIndirimDegeri('');
      setMaxIndirimTutar('');
      setMinKiralamaTutar('');
      setToplamLimit('');
      setMusteriBasinaLimit(1);
      setGecerliBaslangic(tarihYMD(bugun));
      setGecerliBitis(tarihYMD(sonraki));
      setAciklama('');
      setAktif(true);
    }
  }, [acik, kupon]);

  const calisiyor = olustur.isPending || guncelle.isPending;
  const formGecerli =
    /^[A-Z0-9_-]{3,40}$/.test(kod) &&
    /^\d+(\.\d{1,2})?$/.test(indirimDegeri) &&
    aciklama.trim().length >= 3 &&
    gecerliBaslangic &&
    gecerliBitis &&
    new Date(gecerliBitis) > new Date(gecerliBaslangic);

  const gonder = async () => {
    const govde = {
      kod: kod.toUpperCase(),
      tip,
      indirimTipi,
      indirimDegeri,
      maxIndirimTutar: maxIndirimTutar || null,
      minKiralamaTutar: minKiralamaTutar || null,
      paraBirimi: null,
      toplamLimit: toplamLimit ? Number(toplamLimit) : null,
      musteriBasinaLimit,
      gecerliBaslangic: new Date(gecerliBaslangic).toISOString(),
      gecerliBitis: new Date(gecerliBitis).toISOString(),
      sadeceNoktalar: [] as string[],
      sadecePartnerler: [] as string[],
      aciklama: aciklama.trim(),
      aktif,
    };
    try {
      if (duzenleme && kupon) {
        await guncelle.mutateAsync({ id: kupon.id, ...govde });
        toast.success('Kupon güncellendi');
      } else {
        await olustur.mutateAsync(govde);
        toast.success('Kupon oluşturuldu');
      }
      onDegisim(false);
    } catch (err) {
      const m =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'İşlem başarısız';
      toast.error(m);
    }
  };

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[95vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold">
              {duzenleme ? 'Kuponu Düzenle' : 'Yeni Kupon'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                aria-label="Kapat"
                className="text-fume-400 hover:text-fume-700 dark:hover:text-fume-100"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (formGecerli && !calisiyor) void gonder();
            }}
            className="space-y-4"
          >
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Etiket htmlFor="kod">
                  Kod <span className="text-red-500">*</span>
                </Etiket>
                <div className="flex gap-1">
                  <Girdi
                    id="kod"
                    value={kod}
                    onChange={(e) => setKod(e.target.value.toUpperCase())}
                    required
                    placeholder="YAZ2026"
                    className="flex-1 font-mono uppercase"
                  />
                  <Buton
                    type="button"
                    stil="cizgili"
                    boyut="simge"
                    aria-label="Rastgele"
                    onClick={() => setKod(rastgeleKod())}
                  >
                    <Dice5 size={14} />
                  </Buton>
                </div>
              </div>
              <div>
                <Etiket htmlFor="tip">Tip</Etiket>
                <select
                  id="tip"
                  value={tip}
                  onChange={(e) => setTip(e.target.value as KuponTipi)}
                  className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 mt-1 h-11 w-full rounded-lg border bg-white px-3 text-sm"
                >
                  {TIP_SECENEK.map((t) => (
                    <option key={t.d} value={t.d}>
                      {t.e}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Etiket htmlFor="indirimTipi">İndirim Tipi</Etiket>
                <select
                  id="indirimTipi"
                  value={indirimTipi}
                  onChange={(e) => setIndirimTipi(e.target.value as KuponIndirimTipi)}
                  className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 mt-1 h-11 w-full rounded-lg border bg-white px-3 text-sm"
                >
                  <option value="YUZDE">Yüzde (%)</option>
                  <option value="SABIT">Sabit tutar</option>
                </select>
              </div>
              <div>
                <Etiket htmlFor="deg">
                  Değer <span className="text-red-500">*</span>
                </Etiket>
                <Girdi
                  id="deg"
                  value={indirimDegeri}
                  onChange={(e) => setIndirimDegeri(e.target.value)}
                  required
                  placeholder={indirimTipi === 'YUZDE' ? '20' : '15.00'}
                />
              </div>
              <div>
                <Etiket htmlFor="max">Max indirim (ops)</Etiket>
                <Girdi
                  id="max"
                  value={maxIndirimTutar}
                  onChange={(e) => setMaxIndirimTutar(e.target.value)}
                  placeholder="50.00"
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Etiket htmlFor="min">Min kiralama (ops)</Etiket>
                <Girdi
                  id="min"
                  value={minKiralamaTutar}
                  onChange={(e) => setMinKiralamaTutar(e.target.value)}
                  placeholder="20.00"
                />
              </div>
              <div>
                <Etiket htmlFor="lim">Toplam limit (ops)</Etiket>
                <Girdi
                  id="lim"
                  type="number"
                  value={toplamLimit}
                  onChange={(e) => setToplamLimit(e.target.value)}
                  min={1}
                  placeholder="100"
                />
              </div>
              <div>
                <Etiket htmlFor="mbl">Müşteri başına</Etiket>
                <Girdi
                  id="mbl"
                  type="number"
                  value={musteriBasinaLimit}
                  onChange={(e) => setMusteriBasinaLimit(Number(e.target.value))}
                  min={1}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Etiket htmlFor="gb">
                  Geçerli başlangıç <span className="text-red-500">*</span>
                </Etiket>
                <Girdi
                  id="gb"
                  type="datetime-local"
                  value={gecerliBaslangic}
                  onChange={(e) => setGecerliBaslangic(e.target.value)}
                  required
                />
              </div>
              <div>
                <Etiket htmlFor="gbi">
                  Geçerli bitiş <span className="text-red-500">*</span>
                </Etiket>
                <Girdi
                  id="gbi"
                  type="datetime-local"
                  value={gecerliBitis}
                  onChange={(e) => setGecerliBitis(e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <Etiket htmlFor="aciklama">
                Açıklama <span className="text-red-500">*</span>
              </Etiket>
              <Girdi
                id="aciklama"
                value={aciklama}
                onChange={(e) => setAciklama(e.target.value)}
                placeholder="Yaz kampanyası — %20 indirim"
                required
              />
            </div>

            <label className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 flex h-11 cursor-pointer items-center gap-2 rounded-lg border bg-white px-3 text-sm">
              <input type="checkbox" checked={aktif} onChange={(e) => setAktif(e.target.checked)} />
              Aktif
            </label>

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Buton stil="cizgili" boyut="orta" disabled={calisiyor}>
                  İptal
                </Buton>
              </Dialog.Close>
              <Buton
                stil="birincil"
                boyut="orta"
                type="submit"
                disabled={!formGecerli || calisiyor}
              >
                {calisiyor ? 'Kaydediliyor...' : duzenleme ? 'Güncelle' : 'Oluştur'}
              </Buton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
